// The overlay GEOMETRY — the pure half of the field's pane overlay interaction
// (lane L-OVL). The field (canon 75: the holographic terminal, route /field) is
// one surface with four states; an opened challenge is PROJECTED over the feed
// as an overlay, and in split two challenges are two live panes. This module is
// the geometry that lets a person MOVE, RESIZE and STACK those panes as a
// first-class interaction: the rectangle maths, the clamp that keeps a pane on
// the field, the resize that honours a minimum, the hit-test that says which
// edge/corner a pointer grabbed, and the z-order that brings a pane to the front
// when it is touched.
//
// It is PURE (canon 91 §1: framework-free, and the geometry stays testable with
// no DOM): every function here is a plain function of plain data — no document,
// no element, no retained state, no coordinate ever leaves as anything but a
// number a test can assert. The DOM half (overlay.ts) feeds live pointer
// positions in and writes the results back onto the panes; this half decides
// what the new rectangle and stack ARE. Mirrors the split the stroke recognizer
// already keeps (recognizer.ts pure + gestures.ts DOM), so the field's two
// pointer interactions share one shape.
//
// No geometry here draws a cursor, a hand or a figure (canon 83 §1.4): these are
// rectangles and z-indices, the raw material the DOM layer renders as crisp
// charged panes with an active glow. Nothing visual is decided here.

// ---- rectangles -------------------------------------------------------------

/** A pane's rectangle on the field, in field (CSS pixel) coordinates: its top-
 *  left corner and its size. The field's own origin is (0,0); a pane is placed
 *  relative to it. All overlay maths is in this one space so the DOM layer only
 *  has to map the field's bounding box once. */
export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The field's inner bounds a pane must stay within (its content box size). A
 *  pane is clamped so it never leaves the field — the projection stays ON the
 *  terminal, never lost past its edge (canon 75: the feed is always there under
 *  the projected challenges). */
export interface Bounds {
  width: number;
  height: number;
}

/** The smallest a pane may be made (field pixels). A challenge view has a ring,
 *  a headline and its takes; below this it stops being legible, so resize floors
 *  here rather than letting a pane collapse to nothing. Width/height are the two
 *  floors; the DOM layer passes the pane's own measured minimum when it has one. */
export interface MinSize {
  width: number;
  height: number;
}

/** The default floor when a pane declares none. Chosen so a challenge ring plus
 *  one line of its headline still reads. */
export const DEFAULT_MIN: MinSize = { width: 220, height: 180 };

// ---- clamp: keep a pane on the field ---------------------------------------

/**
 * Clamp a pane's rectangle so it stays inside the field bounds. A pane may not
 * be positioned so its top-left leaves the field, nor so it extends past the
 * far edge; if the pane is larger than the field in an axis it is pinned to the
 * origin of that axis (so its top-left is always reachable). Size is NOT changed
 * here — clamp is for a MOVE; resize has its own floor. Pure.
 */
export function clampToField(rect: Rect, bounds: Bounds): Rect {
  const maxX = Math.max(0, bounds.width - rect.width);
  const maxY = Math.max(0, bounds.height - rect.height);
  return {
    x: clamp(rect.x, 0, maxX),
    y: clamp(rect.y, 0, maxY),
    width: rect.width,
    height: rect.height,
  };
}

/** Move a pane by a pointer delta and clamp the result to the field. The delta
 *  is the pointer's travel since the drag began applied to the rectangle the
 *  drag began from, so a drag is absolute from its origin (no drift from
 *  accumulated rounding). Pure. */
export function moveBy(origin: Rect, dx: number, dy: number, bounds: Bounds): Rect {
  return clampToField({ x: origin.x + dx, y: origin.y + dy, width: origin.width, height: origin.height }, bounds);
}

// ---- resize: the eight grips, honouring a floor and the field edge ----------

/** Which part of a pane's frame a resize grabbed. The four edges move one side;
 *  the four corners move two. "move" is the whole pane (the grip bar), handled by
 *  moveBy, and is here so a single hit-test can answer every overlay grab. */
export type Handle =
  | "move"
  | "n"
  | "s"
  | "e"
  | "w"
  | "ne"
  | "nw"
  | "se"
  | "sw";

/** True when the handle changes the pane's north (top) edge. */
function movesNorth(h: Handle): boolean {
  return h === "n" || h === "ne" || h === "nw";
}
function movesSouth(h: Handle): boolean {
  return h === "s" || h === "se" || h === "sw";
}
function movesWest(h: Handle): boolean {
  return h === "w" || h === "nw" || h === "sw";
}
function movesEast(h: Handle): boolean {
  return h === "e" || h === "ne" || h === "se";
}

/**
 * Resize a pane from the rectangle the drag began at, by a pointer delta, for
 * the grabbed handle. The moved edge(s) follow the pointer; the opposite edge(s)
 * stay fixed (an anchor), so resizing from the east edge grows the pane
 * rightward while its left edge holds. The result honours the floor (a pane
 * cannot be made smaller than `min` — the moved edge stops, it never crosses the
 * anchor) and the field edge (a moved edge cannot leave the field). Pure.
 *
 * The maths is done as "the moved edge's new field coordinate, clamped between
 * the field edge and the floor relative to the fixed anchor", then rebuilt into
 * a rectangle — so every case (edge or corner, growing or shrinking) is the same
 * two one-dimensional clamps, not eight special cases.
 */
export function resizeBy(
  origin: Rect,
  handle: Handle,
  dx: number,
  dy: number,
  bounds: Bounds,
  min: MinSize = DEFAULT_MIN,
): Rect {
  let { x, y, width, height } = origin;
  const right = origin.x + origin.width;
  const bottom = origin.y + origin.height;

  if (movesEast(handle)) {
    // Right edge moves; left anchor is origin.x. New right in [x+min, bounds.width].
    const newRight = clamp(right + dx, origin.x + min.width, bounds.width);
    width = newRight - origin.x;
  } else if (movesWest(handle)) {
    // Left edge moves; right anchor is `right`. New left in [0, right-min].
    const newLeft = clamp(origin.x + dx, 0, right - min.width);
    x = newLeft;
    width = right - newLeft;
  }

  if (movesSouth(handle)) {
    const newBottom = clamp(bottom + dy, origin.y + min.height, bounds.height);
    height = newBottom - origin.y;
  } else if (movesNorth(handle)) {
    const newTop = clamp(origin.y + dy, 0, bottom - min.height);
    y = newTop;
    height = bottom - newTop;
  }

  return { x, y, width, height };
}

// ---- hit-test: which handle a point near the frame grabbed ------------------

/**
 * Decide which resize handle (if any) a point grabbed, given the pane rectangle
 * and a grab margin (how close to an edge counts as grabbing it). A point within
 * `margin` of a corner grabs that corner; within `margin` of one edge grabs that
 * edge; strictly inside grabs nothing (null — the pane body, where a stroke or a
 * tap belongs, not a resize). A point outside the pane entirely is null too.
 * Pure: it is told the rectangle, it does not read the DOM.
 *
 * Corners win over edges (a point near a corner is near two edges), which is why
 * the near-flags are combined into a corner first.
 */
export function handleAt(rect: Rect, px: number, py: number, margin = 12): Handle | null {
  const left = rect.x;
  const right = rect.x + rect.width;
  const top = rect.y;
  const bottom = rect.y + rect.height;

  // Outside the pane (beyond the grab margin on any side) is no handle.
  if (px < left - margin || px > right + margin || py < top - margin || py > bottom + margin) {
    return null;
  }

  const nearW = Math.abs(px - left) <= margin;
  const nearE = Math.abs(px - right) <= margin;
  const nearN = Math.abs(py - top) <= margin;
  const nearS = Math.abs(py - bottom) <= margin;

  if (nearN && nearW) return "nw";
  if (nearN && nearE) return "ne";
  if (nearS && nearW) return "sw";
  if (nearS && nearE) return "se";
  if (nearN) return "n";
  if (nearS) return "s";
  if (nearW) return "w";
  if (nearE) return "e";
  return null; // strictly inside: the pane body
}

// ---- z-order: the stack ----------------------------------------------------

/**
 * Bring one pane to the front of the stack. Given the current z-order (pane ids,
 * back-to-front) and the id touched, return the new order with that id moved to
 * the end (front). An id not already in the order is appended (a newly mounted
 * pane arrives on top). The order is a list of ids, not z-index numbers — the
 * DOM layer assigns z-index from the list position, so the stack is always a
 * clean contiguous run with no gaps and no collisions, however many times panes
 * are raised. Pure.
 */
export function bringToFront(order: readonly string[], id: string): string[] {
  const without = order.filter((o) => o !== id);
  without.push(id);
  return without;
}

/**
 * The z-index a pane gets from its position in the back-to-front order. Back is
 * `base`; each step forward adds one. An id not in the order gets `base` (it is
 * treated as backmost until it is raised). The base is well above the field's
 * own resting content and below any always-on-top chrome, so a raised pane sits
 * over its siblings without escaping the field layer. Pure.
 */
export function zIndexOf(order: readonly string[], id: string, base = 10): number {
  const i = order.indexOf(id);
  return i < 0 ? base : base + i;
}

/** The id at the front of the stack (the topmost pane), or null when empty. */
export function frontOf(order: readonly string[]): string | null {
  return order.length ? order[order.length - 1] : null;
}

// ---- shared ----------------------------------------------------------------

/** Clamp a scalar into [lo, hi]. When the range is inverted (hi < lo, which
 *  happens when a pane is wider than the field), lo wins — the pane pins to the
 *  near edge rather than snapping to a negative far edge. */
export function clamp(v: number, lo: number, hi: number): number {
  if (hi < lo) return lo;
  return v < lo ? lo : v > hi ? hi : v;
}
